import { useTranslations } from 'next-intl';
import type { ReactElement } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface ClaimIdentityGateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  professionalName: string;
  isSubmitting: boolean;
  onConfirmSelf: () => void;
  onDeclineSelf: () => void;
}

/**
 * Self-declaration gate (#8306) shown before the claim-verification email
 * fires. A directory visitor who isn't the professional could otherwise
 * trigger a confusing, unsolicited email to the real lawyer's SAK-registry
 * address. Asking "is this you?" first routes non-matches to
 * ClaimThirdPartyNoticeCard instead of sending anything.
 */
export function ClaimIdentityGateModal({
  open,
  onOpenChange,
  professionalName,
  isSubmitting,
  onConfirmSelf,
  onDeclineSelf,
}: ClaimIdentityGateModalProps): ReactElement {
  const t = useTranslations('Lawyers.claim');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('identityGateTitle', { name: professionalName })}
          </DialogTitle>
          <DialogDescription>{t('identityGateDescription')}</DialogDescription>
        </DialogHeader>
        <DialogFooter className='gap-2 sm:gap-2'>
          <Button
            variant='outline'
            onClick={onDeclineSelf}
            disabled={isSubmitting}
          >
            {t('identityGateConfirmNo')}
          </Button>
          <Button
            variant='brand'
            onClick={onConfirmSelf}
            disabled={isSubmitting}
          >
            {t('identityGateConfirmYes', { name: professionalName })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
